import * as React from 'react';

import {
    AutoScrollbars,
    OverlayFormInput,
    FileChooser,
    Button,
    OverlayFormTextArea
} from 'components';

import { actions as appActions } from 'services/app/app-service';
import { UpdateDataOverlay } from 'services/overlay-windows/overlay-windows-service';
import { Captcha } from 'components/captcha';
import { useSelector } from 'react-redux';
import { getOperationSafe } from 'selectors';
import { IState } from 'services/root-reducer';
import { useTranslation, Trans } from 'react-i18next';

import './data-update-form.scss';
import { IOperationsState } from 'services/operations/operations-reducer';
import { EOperationStatus, IOperation } from 'types';
import { validateEmail } from 'utils/validation';

export interface IDataUpdateFormPayload {
    fio: string;
    email: string;
    message: string;
    attachments: File[];
    captcha_key: string;
    security_code: string;
    captcha_value: string;
    object: string;
}

interface IDataUpdateFormProps {
    onSubmit: (payload: IDataUpdateFormPayload) => void;
}

export const DataUpdateForm = (props: IDataUpdateFormProps) => {
    let [ fio_field, setFioField ] = React.useState<string>('');
    let [ email_field, setEmailField ] = React.useState<string>('');
    let [ message_field, setMessageField ] = React.useState<string>('');
    let [ attachments_field, setAttachmentsField ] = React.useState<File[]>([]);
    let [ captcha_value_field, setCaptchaValue ] = React.useState<string>('');

    let [ fio_field_error, setFioFieldError ] = React.useState<string>('');
    let [ email_field_error, setEmailFieldError ] = React.useState<string>('');
    let [ message_field_error, setMessageFieldError ] = React.useState<string>('');
    let [ captcha_value_field_error, setCaptchaValueError ] = React.useState<string>('');

    let updateOverlayOperation: UpdateDataOverlay = useSelector(
        (state: IState) => getOperationSafe(state, 'UPDATE_DATA_OVERLAY')
    );
    let submitOverlayOperation: IOperation = useSelector(
        (state: IState) => getOperationSafe(state, 'SUBMIT_DATA_UPDATE_OVERLAY')
    );
    const { t } = useTranslation();

    let captchaOperation: IOperationsState['CAPTCHA'] = useSelector(
        (state: IState) => getOperationSafe(state, 'CAPTCHA')
    );

    React.useEffect(() => {
        appActions.loadNewCaptcha();
    }, []);

    React.useEffect(() => {
        if (submitOverlayOperation.status === EOperationStatus.FAIL) {
            setCaptchaValue('');
            setCaptchaValueError(t('Неправильная капча'));
        }
    }, [ submitOverlayOperation.status ]);

    const validateForm = () => {
        let hasErrors = false;

        if (fio_field.length === 0) {
            setFioFieldError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setFioFieldError('');
        }
        if (email_field.length === 0) {
            setEmailFieldError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setEmailFieldError('');
        }
        if (!validateEmail(email_field)) {
            setEmailFieldError(t('Email указан неверно'));
            hasErrors = true;
        } else {
            setEmailFieldError('');
        }
        if (message_field.length === 0) {
            setMessageFieldError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setMessageFieldError('');
        }
        if (captcha_value_field.length === 0) {
            setCaptchaValueError(t('Поле обязательно для заполнения'));
            hasErrors = true;
        } else {
            setCaptchaValueError('');
        }

        return hasErrors;
    };

    const onSendButtonClick = () => {
        let hasErrors = validateForm();

        if (!hasErrors) {
            props.onSubmit({
                fio: fio_field,
                email: email_field,
                message: message_field,
                attachments: attachments_field,
                security_code: __SECURITY_CODE__,
                captcha_key: captchaOperation.data.CAPTCHA_KEY,
                captcha_value: captcha_value_field,
                object: JSON.stringify(updateOverlayOperation.data.object)
            });
        }
    };

    return (
        <div className="data-update-form-wrapper">
            <AutoScrollbars className='padding-fix'>
                <div className="form-header">
                    {t('Заявка на обновление данных')}
                </div>
                <div className="form-body">
                    <OverlayFormInput
                        required
                        error={fio_field_error}
                        placeholder={t('ФИО')}
                        wrapperClassname='submit-application-input'
                        value={fio_field}
                        onChange={setFioField}
                    />

                    <OverlayFormInput
                        required
                        error={email_field_error}
                        placeholder={t('Email')}
                        wrapperClassname='submit-application-input'
                        value={email_field}
                        onChange={setEmailField}
                    />

                    <OverlayFormTextArea
                        required
                        error={message_field_error}
                        placeholder={t('Требования к площадке')}
                        wrapperClassname='submit-application-input'
                        value={message_field}
                        onChange={setMessageField}
                    />

                    <FileChooser
                        files={attachments_field}
                        onAdd={(file) => {
                            setAttachmentsField([
                                ...attachments_field,
                                file
                            ]);
                        }}
                        onRemove={(file) => {
                            let attachments = [ ...attachments_field ];
                            attachments.splice(
                                attachments.indexOf(file),
                                1
                            );
                            setAttachmentsField(attachments);
                        }}
                    />

                    <div
                        className="license-agreement"
                        dangerouslySetInnerHTML={{
                            __html: t('LICENSE_AGREEMENT')
                        }}
                    />

                    <div className="form-footer">
                        <div className="footer-actions-wrapper">
                            <Captcha
                                captchaOperation={captchaOperation}
                                renderInput={() => (
                                    <OverlayFormInput
                                        hideErrorMessage
                                        variant='underlined'
                                        error={captcha_value_field_error}
                                        value={captcha_value_field}
                                        placeholder={t('Введите слово')}
                                        onChange={(value) => {
                                            setCaptchaValue(value);
                                        }}
                                    />
                                )}
                            />

                            <Button
                                text={t('Отправить')}
                                onClick={onSendButtonClick}
                            />
                        </div>
                    </div>
                </div>
            </AutoScrollbars>
        </div>
    );
};
